/**
 * TransactionTagEditor Component
 *
 * Edit tags on a transaction with autocomplete and inline creation.
 * Displays current tags as chips/badges with colors and source indicators.
 */

import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { Plus, Search, X } from 'lucide-react';
import type { Tag, Tagging, TagType } from '@/types/accountingTags';
import { TagBadge } from './TagBadge';

export interface TransactionTagEditorProps {
  /** Current taggings on the transaction */
  taggings: Tagging[];
  /** All available tags for autocomplete */
  availableTags: Tag[];
  /** Callback when a tag is added */
  onAddTag: (tag: Tag) => void;
  /** Callback when a tag is removed */
  onRemoveTag: (tagging: Tagging) => void;
  /** Callback when a new tag is created */
  onCreateTag?: (tagType: TagType, value: string) => void;
  /** Whether the editor is disabled */
  disabled?: boolean;
  /** Compact mode for inline use */
  compact?: boolean;
  /** Start with dropdown open (for inline cell editing) */
  autoOpen?: boolean;
}

/**
 * Tag type display configuration
 */
const TAG_TYPE_CONFIG: Record<TagType, { label: string; color: string }> = {
  vendor: { label: 'Vendor', color: 'bg-orange-100 text-orange-800' },
  recurring: { label: 'Recurring', color: 'bg-blue-100 text-blue-800' },
  trip: { label: 'Trip', color: 'bg-pink-100 text-pink-800' },
  project: { label: 'Project', color: 'bg-amber-100 text-amber-800' },
  shared: { label: 'Shared', color: 'bg-purple-100 text-purple-800' },
  tax: { label: 'Tax', color: 'bg-green-100 text-green-800' },
};

/**
 * TransactionTagEditor provides a UI for managing tags on a transaction.
 *
 * @example
 * <TransactionTagEditor
 *   taggings={transaction.taggings}
 *   availableTags={allTags}
 *   onAddTag={handleAddTag}
 *   onRemoveTag={handleRemoveTag}
 * />
 */
export function TransactionTagEditor({
  taggings,
  availableTags,
  onAddTag,
  onRemoveTag,
  onCreateTag,
  disabled = false,
  compact = false,
  autoOpen = false,
}: TransactionTagEditorProps) {
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<TagType | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get IDs of already-applied tags
  const appliedTagIds = new Set(taggings.map((t) => t.tagId));

  // Filter available tags
  const filteredTags = availableTags.filter((tag) => {
    // Exclude already-applied tags
    if (appliedTagIds.has(tag.id)) return false;

    // Filter by type if selected
    if (selectedType && tag.tagType !== selectedType) return false;

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesValue = tag.value.toLowerCase().includes(query);
      const matchesDisplay = tag.displayName?.toLowerCase().includes(query);
      return matchesValue || matchesDisplay;
    }

    return true;
  });

  // Group filtered tags by type
  const groupedTags = filteredTags.reduce(
    (acc, tag) => {
      if (!acc[tag.tagType]) {
        acc[tag.tagType] = [];
      }
      acc[tag.tagType].push(tag);
      return acc;
    },
    {} as Record<string, Tag[]>
  );

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleAddTag = (tag: Tag) => {
    onAddTag(tag);
    setSearchQuery('');
    setSelectedType(null);
  };

  const handleCreateNewTag = () => {
    if (!onCreateTag || !searchQuery.trim()) return;

    // Parse the search query for type:value format
    const parts = searchQuery.split(':');
    if (parts.length >= 2) {
      const tagType = parts[0] as TagType;
      const value = parts.slice(1).join(':');
      if (TAG_TYPE_CONFIG[tagType] && value) {
        onCreateTag(tagType, value.trim());
        setSearchQuery('');
        setIsOpen(false);
      }
    }
  };

  return (
    <div className={clsx('relative', compact ? 'inline-flex' : 'w-full')} ref={dropdownRef}>
      {/* Current tags */}
      <div className={clsx('flex flex-wrap gap-1.5', !compact && 'mb-2')}>
        {taggings.map((tagging) => (
          <TagBadge
            key={tagging.id}
            tag={tagging.tag}
            size={compact ? 'sm' : 'md'}
            source={tagging.source}
            confidence={tagging.confidence}
            removable={!disabled}
            onRemove={() => onRemoveTag(tagging)}
          />
        ))}

        {/* Add tag button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className={clsx(
            'inline-flex items-center gap-1 rounded-full border border-dashed',
            'text-gray-500 hover:text-gray-700 hover:border-gray-400',
            'transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            compact
              ? 'px-1.5 py-0.5 text-xs border-gray-300'
              : 'px-2 py-0.5 text-sm border-gray-300'
          )}
          aria-label="Add tag"
        >
          <Plus size={compact ? 10 : 14} />
          {!compact && <span>Add tag</span>}
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={clsx(
            'absolute z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200',
            'w-72 max-h-80 overflow-hidden',
            compact ? 'left-0' : 'left-0 right-0'
          )}
          data-testid="tag-editor-dropdown"
        >
          {/* Search input */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tags or type:value..."
                className={clsx(
                  'w-full pl-8 pr-3 py-1.5 text-sm rounded-md',
                  'border border-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500',
                  'outline-none'
                )}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Type filter chips */}
          <div className="p-2 border-b border-gray-100 flex flex-wrap gap-1">
            <button
              type="button"
              onClick={() => setSelectedType(null)}
              className={clsx(
                'px-2 py-0.5 text-xs rounded-full transition-colors',
                selectedType === null
                  ? 'bg-gray-800 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
              data-testid="tag-type-filter"
            >
              All
            </button>
            {(Object.entries(TAG_TYPE_CONFIG) as [TagType, { label: string; color: string }][]).map(
              ([type, config]) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setSelectedType(selectedType === type ? null : type)}
                  className={clsx(
                    'px-2 py-0.5 text-xs rounded-full transition-colors',
                    selectedType === type ? config.color : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  )}
                  data-testid="tag-type-filter"
                >
                  {config.label}
                </button>
              )
            )}
          </div>

          {/* Tag list */}
          <div className="max-h-48 overflow-y-auto p-2">
            {Object.keys(groupedTags).length === 0 ? (
              <div className="py-4 text-center text-sm text-gray-500">
                {searchQuery ? (
                  <>
                    No matching tags found.
                    {onCreateTag && searchQuery.includes(':') && (
                      <button
                        type="button"
                        onClick={handleCreateNewTag}
                        className="block w-full mt-2 text-blue-600 hover:text-blue-700"
                      >
                        Create "{searchQuery}"
                      </button>
                    )}
                  </>
                ) : (
                  'No tags available'
                )}
              </div>
            ) : (
              Object.entries(groupedTags).map(([type, tags]) => (
                <div key={type} className="mb-2 last:mb-0">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider px-1 mb-1">
                    {TAG_TYPE_CONFIG[type as TagType]?.label || type}
                  </div>
                  <div className="space-y-0.5">
                    {tags.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => handleAddTag(tag)}
                        className={clsx(
                          'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left',
                          'hover:bg-gray-50 transition-colors'
                        )}
                        data-testid="tag-option"
                        data-tag-type={tag.tagType}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: tag.color || '#9CA3AF' }}
                        />
                        <span className="text-sm text-gray-700 truncate">
                          {tag.displayName || tag.value}
                        </span>
                        <span className="text-xs text-gray-400 ml-auto">{tag.value}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Create new tag hint */}
          {onCreateTag && (
            <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-500">
              Type <code className="px-1 py-0.5 bg-gray-200 rounded">type:value</code> to create a new
              tag
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TransactionTagEditor;
